import { type ReactNode, useEffect, useState } from "react";
import type { OnboardingStatus } from "@/features/home/onboarding/schemas/onboarding-status.schema";
import { AddUsersWizard } from "@/features/people/components/add-users-wizard";
import { Dialog, DialogContent, DialogTitle } from "@/shared/components/ui/dialog";
import { buildQuestLevels, questXp } from "./onboarding-quest.logic";
import { OnboardingQuestPanel } from "./onboarding-quest-panel";
import { OnboardingReward } from "./onboarding-reward";

type OnboardingImportStage = "intro" | "wizard" | "celebrate";

interface OnboardingImportDialogProps {
  workspaceId: string;
  open: boolean;
  onboardingStatus?: OnboardingStatus;
  welcomeMessage: string;
  onOpenChange: (open: boolean) => void;
  onRemindLater: () => void;
  onImported?: () => void;
  onStartKudos: () => void;
  onStartCheckIn: () => void;
  onDoneCelebrating: () => void;
}

interface OnboardingImportDialogBodyProps {
  stage: OnboardingImportStage;
  onboardingStatus?: OnboardingStatus;
  welcomeMessage: string;
  onStartImport: () => void;
  onStartKudos: () => void;
  onStartCheckIn: () => void;
  onRemindLater: () => void;
  onDoneCelebrating: () => void;
  wizardContent: ReactNode;
}

export function OnboardingImportDialog({
  workspaceId,
  open,
  onboardingStatus,
  welcomeMessage,
  onOpenChange,
  onRemindLater,
  onImported,
  onStartKudos,
  onStartCheckIn,
  onDoneCelebrating,
}: OnboardingImportDialogProps) {
  const [stage, setStage] = useState<OnboardingImportStage>("intro");

  useEffect(() => {
    if (open) return;
    setStage("intro");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const focusTargetId =
      stage === "intro"
        ? "onboarding-import-primary-cta"
        : stage === "wizard"
          ? "add-users-wizard-title"
          : "onboarding-reward-done-button";
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(focusTargetId)?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open, stage]);

  const dialogTitle =
    stage === "intro"
      ? "Level up your workspace"
      : stage === "celebrate"
        ? "Level 1 complete!"
        : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={
          stage === "intro"
            ? "onboarding-import-welcome onboarding-import-description"
            : stage === "celebrate"
              ? "onboarding-reward-description"
              : undefined
        }
        className="flex max-h-[min(720px,90vh)] w-[min(720px,95vw)] max-w-none flex-col gap-0 p-0 sm:max-w-none"
      >
        {dialogTitle ? <DialogTitle className="sr-only">{dialogTitle}</DialogTitle> : null}
        <OnboardingImportDialogBody
          stage={stage}
          onboardingStatus={onboardingStatus}
          welcomeMessage={welcomeMessage}
          onStartImport={() => setStage("wizard")}
          onStartKudos={onStartKudos}
          onStartCheckIn={onStartCheckIn}
          onRemindLater={onRemindLater}
          onDoneCelebrating={() => {
            // Return to the quest panel so the remaining steps are visible
            // instead of re-rendering the celebration.
            setStage("intro");
            onDoneCelebrating();
          }}
          wizardContent={
            stage === "wizard" ? (
              <AddUsersWizard
                workspaceId={workspaceId}
                onBackToIntro={() => setStage("intro")}
                onClose={() => onOpenChange(false)}
                onImported={() => {
                  setStage("celebrate");
                  onImported?.();
                }}
              />
            ) : null
          }
        />
      </DialogContent>
    </Dialog>
  );
}

export function OnboardingImportDialogBody({
  stage,
  onboardingStatus,
  welcomeMessage,
  onStartImport,
  onStartKudos,
  onStartCheckIn,
  onRemindLater,
  onDoneCelebrating,
  wizardContent,
}: OnboardingImportDialogBodyProps) {
  if (stage === "wizard") {
    return <>{wizardContent}</>;
  }

  if (stage === "celebrate") {
    return (
      <OnboardingReward
        levelsCompleted={Math.max(questXp(onboardingStatus), 1)}
        onDone={onDoneCelebrating}
      />
    );
  }

  const levels = buildQuestLevels(onboardingStatus);

  return (
    <OnboardingQuestPanel
      levels={levels}
      welcomeMessage={welcomeMessage}
      onStartImport={onStartImport}
      onStartKudos={onStartKudos}
      onStartCheckIn={onStartCheckIn}
      onRemindLater={onRemindLater}
    />
  );
}
